import classNames from 'classnames'
import { TranslationContext } from 'contexts/TranslationContext'
import PropTypes from 'prop-types'
import React, { useContext, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { LEFT_MENU_WIDTH, MAX_SAFE_ELEMENT_WIDTH, STEP_ZOOM_VALUE, VAL_30_MINUTES_AS_100NS_UNIT_SEC } from 'constant'
import { ViewSettings } from 'config/constants/editorSettings'
import * as PT from '~/PropTypes'
import { pixel2Time, time2Pixel, getWindowWidth } from '~/Util'
import { deleteLayer as deleteLayerAction, setMaxScale, setScale } from '~/actions/timeline'
import FileInput from '~/components/base/FileInputLegacy/FileInput'
import { TimelineLeftOffsetContext } from '~/contexts/TimelineLeftOffsetContext'
import { useAction } from '~/hooks'
import { selectDraggingAssetId, selectHoveringInsertedLayerIds } from '~/selectors'
import { selectViewSettings } from '~/selectors/mainView'
import { Context as TimelineGeometryContext } from '../GeometryContextProvider'
import { Draggable as LayerItem } from '../LayerItem'
import './Layer.scss'
import LayerControls from './LayerControls'
import useLayerDropTarget from './useLayerDropTarget'

// ---

const Layer = props => {
  const {
    assets, layer, layerIndex, scale,
    showDropAreaPlaceholder,
    onClick, onAssetDragStarted,
    onAssetDragEnded,
    onDeniedDrop,
    style,
    className,
    dragRef,
  } = props
  const layerRef = useRef()
  const deleteLayer = useAction(deleteLayerAction)

  const hoveringInsertedLayerIds = useSelector(selectHoveringInsertedLayerIds)
  const draggingAssetId = useSelector(selectDraggingAssetId)
  const viewSettings = useSelector(selectViewSettings)
  const isDragging = !!draggingAssetId

  const controlsRef = React.useRef()

  // ---

  const getItemsLeftPadding = React.useCallback(
    () => controlsRef.current.getBoundingClientRect().right,
    []
  )

  const collectedDnDProps = useLayerDropTarget(layerRef, {
    layerId: layer.id,
    layerIndex,
    scale,
    getDragOffsetLeft: getItemsLeftPadding,
    onDenied: onDeniedDrop,
  })

  const { isHovered, item } = collectedDnDProps

  const [ hoveredFirstly, setHoveredFirstly ] = useState(false)

  useEffect(() => {
    // NOTE: ISSUE http://18.184.210.86/issues/1210
    if (!!item?.type && !isHovered && !assets.length
      && hoveringInsertedLayerIds.includes(layer.id)
      && isDragging) {
      // NOTE: This is for fix when created new layer under hover
      if (hoveredFirstly) {
        // NOTE: timeout for fix react-dnd error "Invariant Violation: Expected to find a valid target"
        setTimeout(() => {
          deleteLayer(layer.id, true)
        })
      } else {
        setHoveredFirstly(true)
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ isHovered, hoveringInsertedLayerIds ])

  // ---

  // Init once. Items are padded by controls block, and controls size/position are constant.
  const [ itemsLeftPadding, setItemsLeftPadding ] = React.useState(0)
  React.useEffect(
    () => {
      setItemsLeftPadding(getItemsLeftPadding())
    },
    [ getItemsLeftPadding ]
  )

  // ---

  const getTimeFromMouseEvent = React.useCallback(
    e => pixel2Time(e.clientX - itemsLeftPadding, scale),
    [ itemsLeftPadding, scale ]
  )

  const mouseDownXRef = useRef(null)

  const handleOnClick = React.useCallback(
    e => {
      e.persist()
      if (mouseDownXRef.current === e.pageX) {
        onClick(e, getTimeFromMouseEvent(e))
      }
    },
    [ getTimeFromMouseEvent, onClick ]
  )

  const handleMouseDown = e => {
    e.persist()
    // protection from asset resizing click
    mouseDownXRef.current = e.pageX
  }

  // ---
  const dispatch = useDispatch()
  const { maxLayerWidth, timelineWidth } = React.useContext(TimelineGeometryContext)
  const layerWidth = useMemo(() => {
    const offset30minAsPixels = time2Pixel(VAL_30_MINUTES_AS_100NS_UNIT_SEC, scale) // 30 minutes
    const halfTimeLine = (getWindowWidth() / 2) + LEFT_MENU_WIDTH // 1/2 timeline width
    const offset = Math.max(halfTimeLine, offset30minAsPixels)
    let layerWidth = Math.max(timelineWidth, maxLayerWidth + itemsLeftPadding) + offset
    const maxTime = pixel2Time(layerWidth, scale)

    let currentScale = Math.log(scale)
    let isChangeScale = false

    while (layerWidth > MAX_SAFE_ELEMENT_WIDTH) {
      currentScale -= STEP_ZOOM_VALUE
      layerWidth = time2Pixel(maxTime, Math.exp(currentScale))
      isChangeScale = true
    }

    currentScale = Math.exp(currentScale)

    if (isChangeScale) {
      dispatch(setScale(currentScale))
      dispatch(setMaxScale(currentScale))
    }

    return layerWidth
  }, [ timelineWidth, scale, maxLayerWidth, itemsLeftPadding, dispatch ])


  const layerItemsRef = useRef(null)
  const { setLayerControlsDOMRectData } = useContext(TimelineLeftOffsetContext)
  const { t } = useContext(TranslationContext)

  useLayoutEffect(() => {
    setLayerControlsDOMRectData(() => controlsRef.current?.getBoundingClientRect())
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ item?.id ])

  const getInputTitle = () => <p>{t('TIMELINE_TEXT_DRAG_N_DROP')}</p>

  const showDropArea = showDropAreaPlaceholder
    && Boolean(viewSettings[ViewSettings.showDropPlaceHolderTimeline])

  return (
    <div
      className={classNames('layer', className)}
      ref={layerRef}
      style={{
        ...style,
        width: layerWidth,
      }}
    >
      <LayerControls
        layerId={layer.id}
        ref={controlsRef}
        dragRef={dragRef}
      />

      <div
        ref={layerItemsRef}
        className="layer__items"
        onClick={handleOnClick}
        onMouseDown={handleMouseDown}
      >
        {!layer.visible && <div className="layer__mask" />}
        <If condition={showDropArea}>
          <div className="layer__import">
            <FileInput title={getInputTitle()} withIcon disabled />
          </div>
        </If>
        {assets.map(asset => (
          <LayerItem
            key={asset.id}
            asset={asset}
            onDragStarted={onAssetDragStarted}
            onDragEnded={onAssetDragEnded}
            onTranslate={t}
          />
        ))}
      </div>
    </div>
  )
}

Layer.propTypes = {
  assets: PropTypes.arrayOf(PT.LayerAsset).isRequired,
  layer: PT.Layer.isRequired,
  layerIndex: PropTypes.number.isRequired,
  scale: PropTypes.number.isRequired,

  showDropAreaPlaceholder: PropTypes.bool,

  onClick: PropTypes.func,
  onAssetDragStarted: PropTypes.func,
  onAssetDragEnded: PropTypes.func,
  onDeniedDrop: PropTypes.func,
  dragRef: PT.RefObject.isRequired,
}

Layer.defaultProps = {
  showDropAreaPlaceholder: false,
  onClick: () => { },
  onAssetDragStarted: () => { },
  onAssetDragEnded: () => { },
  onDeniedDrop: () => { },
}

// ---

export default React.memo(Layer)
